import supabase from '../supabase/supabase'

/**
 * Compliance Service
 * Handles all compliance-related database operations
 */

/**
 * Get current user's tenant ID
 */
const getCurrentTenantId = async () => {
  const { data: { user } } = await supabase.auth.getUser()
  
  if (!user) {
    throw new Error('No authenticated user')
  }

  const { data, error } = await supabase
    .from('tenant_members')
    .select('tenant_id')
    .eq('user_id', user.id)
    .single()

  if (error) throw error
  
  return data.tenant_id
}

/**
 * Get overall compliance score and summary
 */
export const getOverallComplianceScore = async () => {
  try {
    const tenantId = await getCurrentTenantId()

    const { data, error } = await supabase
      .rpc('calculate_overall_compliance_score', {
        p_tenant_id: tenantId
      })

    if (error) throw error

    return data?.[0] || {
      overall_score: 0,
      legal_acas_score: 0,
      workflow_score: 0,
      timeliness_score: 0,
      total_cases: 0,
      cases_at_risk: 0,
      non_compliant_cases: 0,
      incomplete_legal_actions: 0,
      incomplete_acas_actions: 0
    }
  } catch (error) {
    console.error('Error fetching overall compliance score:', error)
    throw error
  }
}

/**
 * Get compliance breakdown by area
 */
export const getComplianceByArea = async () => {
  try {
    const tenantId = await getCurrentTenantId()

    const { data, error } = await supabase
      .rpc('get_compliance_by_area', {
        p_tenant_id: tenantId
      })

    if (error) throw error

    return data || []
  } catch (error) {
    console.error('Error fetching compliance by area:', error)
    throw error
  }
}

/**
 * Get organizational compliance metrics
 */
export const getOrganizationalMetrics = async () => {
  try {
    const tenantId = await getCurrentTenantId()

    const { data, error } = await supabase
      .from('organizational_compliance_metrics')
      .select('*')
      .eq('tenant_id', tenantId)
      .single()

    if (error) throw error

    return data || {}
  } catch (error) {
    console.error('Error fetching organizational metrics:', error)
    throw error
  }
}

/**
 * Get legal and ACAS compliance metrics
 */
export const getLegalAcasCompliance = async () => {
  try {
    const tenantId = await getCurrentTenantId()

    const { data, error } = await supabase
      .from('legal_acas_compliance')
      .select('*')
      .eq('tenant_id', tenantId)

    if (error) throw error

    return data || []
  } catch (error) {
    console.error('Error fetching legal/ACAS compliance:', error)
    throw error
  }
}

/**
 * Get case type specific compliance
 */
export const getCaseTypeCompliance = async () => {
  try {
    const tenantId = await getCurrentTenantId()

    const { data, error } = await supabase
      .from('case_type_compliance')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('compliance_score', { ascending: true }) // Show worst first

    if (error) throw error

    return data || []
  } catch (error) {
    console.error('Error fetching case type compliance:', error)
    throw error
  }
}

/**
 * Get workflow compliance metrics
 */
export const getWorkflowCompliance = async () => {
  try {
    const tenantId = await getCurrentTenantId()

    const { data, error } = await supabase
      .from('workflow_compliance')
      .select('*')
      .eq('tenant_id', tenantId)

    if (error) throw error

    return data || []
  } catch (error) {
    console.error('Error fetching workflow compliance:', error)
    throw error
  }
}

/**
 * Get compliance summary
 */
export const getComplianceSummary = async () => {
  try {
    const tenantId = await getCurrentTenantId()

    const { data, error } = await supabase
      .from('compliance_summary')
      .select('*')
      .eq('tenant_id', tenantId)
      .single()

    if (error) throw error

    return data || {}
  } catch (error) {
    console.error('Error fetching compliance summary:', error)
    throw error
  }
}

/**
 * Get all compliance data (comprehensive)
 */
export const getAllComplianceData = async () => {
  try {
    const [
      overallScore,
      complianceAreas,
      orgMetrics,
      legalAcas,
      caseTypes,
      workflows,
      summary
    ] = await Promise.all([
      getOverallComplianceScore(),
      getComplianceByArea(),
      getOrganizationalMetrics(),
      getLegalAcasCompliance(),
      getCaseTypeCompliance(),
      getWorkflowCompliance(),
      getComplianceSummary()
    ])

    return {
      overallScore,
      complianceAreas,
      orgMetrics,
      legalAcas,
      caseTypes,
      workflows,
      summary
    }
  } catch (error) {
    console.error('Error fetching all compliance data:', error)
    throw error
  }
}

/**
 * Get tribunal risk alerts
 */
export const getTribunalRiskAlerts = async () => {
  try {
    const tenantId = await getCurrentTenantId()

    const { data, error } = await supabase
      .from('tribunal_risk_alerts')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('sort_priority', { ascending: true })
      .order('days_overdue', { ascending: false })
      .limit(20)

    if (error) throw error

    return data || []
  } catch (error) {
    console.error('Error fetching tribunal risk alerts:', error)
    throw error
  }
}

/**
 * Get staff risk distribution for the current tenant
 * Returns counts of staff by risk level (low, medium, high)
 */
export const getStaffRiskDistribution = async () => {
  try {
    const tenantId = await getCurrentTenantId()

    const { data, error } = await supabase
      .from('staff')
      .select('risk_level')
      .eq('tenant_id', tenantId)
      .is('deleted_at', null)

    if (error) throw error

    // Count by risk level
    const counts = {
      low: 0,
      medium: 0,
      high: 0
    }

    const staffList = data || []
    staffList.forEach(staff => {
      const level = (staff.risk_level || 'low').toLowerCase()
      if (Object.hasOwn(counts, level)) {
        counts[level]++
      } else {
        counts.low++ // Default unknown levels to low
      }
    })

    // Format for pie chart
    return [
      { name: 'Low Risk', value: counts.low, color: '#10b981' },
      { name: 'Medium Risk', value: counts.medium, color: '#f59e0b' },
      { name: 'High Risk', value: counts.high, color: '#ef4444' }
    ]
  } catch (error) {
    console.error('Error fetching staff risk distribution:', error)
    // Return empty distribution on error
    return [
      { name: 'Low Risk', value: 0, color: '#10b981' },
      { name: 'Medium Risk', value: 0, color: '#f59e0b' },
      { name: 'High Risk', value: 0, color: '#ef4444' }
    ]
  }
}

export default {
  getOverallComplianceScore,
  getComplianceByArea,
  getOrganizationalMetrics,
  getLegalAcasCompliance,
  getCaseTypeCompliance,
  getWorkflowCompliance,
  getComplianceSummary,
  getAllComplianceData,
  getTribunalRiskAlerts,
  getStaffRiskDistribution
}
